import { Dialog } from "@kobalte/core/dialog";
import type { AssetId } from "@storytell/types";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { TbBellRinging, TbCircleCheck, TbX } from "solid-icons/tb";
import { Match, Switch, createSignal } from "solid-js";
import { useWire } from "~/wire";
import { lifecycleStateGrouped, lifecycleStateToShort } from "../utils/lifecycleStateMappers";

export const AssetsProcessingWaitModal = () => {
  const wire = useWire();

  const [variant, setVariant] = createSignal<"ask" | "previously-denied" | "allowed">("ask");

  const notReadyAssets = () => {
    const ids = wire.chat.scope.assets() as AssetId[];
    const assets = ids.filter((id) => {
      const asset = wire.assets.get(id);
      if (!asset) return false;
      return ["processing", "uploading"].includes(lifecycleStateGrouped(asset.status.state));
    });

    return assets;
  };

  const allNotReady = () => {
    return notReadyAssets().length + wire.chat.scope.uploadingAssets().length;
  };

  const closeModal = () => {
    wire.ui.processingAssetsGate.closeModal();
    // There's a fade-out animation on the modal
    // Wait for it to finish before resetting the variant so there's
    // no flash of different text as it's closing
    setTimeout(() => {
      setVariant("ask");
    }, 300);
  };

  const onPromptForNotifications = () => {
    if (Notification.permission === "denied") {
      setVariant("previously-denied");
      return;
    }
    Notification.requestPermission().then((result) => {
      if (result === "denied") {
        setVariant("previously-denied");
        return;
      }

      if (result === "granted") {
        setVariant("allowed");

        const interval = setInterval(() => {
          let haveSomeFailed = false;
          const assetIds = notReadyAssets();
          const allReady = assetIds.every((id) => {
            const asset = wire.assets.get(id);
            // If we can't find the asset anymore, assume it's been deleted and out of the scope
            if (!asset) return true;

            const lifecycle = lifecycleStateGrouped(asset.status.state);
            if (lifecycle === "error" || lifecycle === "insufficient credits") haveSomeFailed = true;
            // Count any state that's not processing or uploading as done, even if some assets
            // might have errored out
            return !["processing", "uploading"].includes(lifecycle);
          });

          if (allReady && wire.chat.scope.uploadingAssets().length === 0) {
            clearInterval(interval);
            closeModal();

            const notification = new Notification("Storytell", {
              body: `Your assets are ready to chat with.${haveSomeFailed ? "\nBut some of them failed to process." : ""}`,
              icon: "/images/storytell-logo-icon.svg",
            });

            notification.onclick = () => {
              notification.close();
              window.focus();
            };
          }
          // Check every 10 seconds
        }, 10 * 1000);

        return;
      }
    });
  };

  return (
    <AnimateChildren
      show={wire.ui.processingAssetsGate.isModalOpen()}
      enter={{ opacity: [0, 1], y: [10, 0] }}
      exit={{ opacity: [1, 0], y: [0, 10] }}
    >
      {(anim) => (
        <Dialog
          open={anim.isMounted()}
          onOpenChange={(val) => {
            if (!val) {
              closeModal();
            }
          }}
        >
          <Dialog.Portal>
            <Dialog.Overlay class="fixed z-90 inset-0 bg-black/50 dark:bg-white/10" />
            <div ref={anim.ref} class="fixed z-90 inset-0 flex items-center justify-center">
              <Dialog.Content class="max-h-[80vh] relative w-screen text-primary-text max-w-xl bg-panel overflow-y-auto rounded-xl p-8">
                <Dialog.CloseButton as={StButton} class="absolute top-4 right-4">
                  <TbX />
                </Dialog.CloseButton>

                <Switch>
                  <Match when={variant() === "ask"}>
                    <h3 class="text-lg font-medium mb-6">
                      {allNotReady()} asset{allNotReady() === 1 ? "" : "s"} {allNotReady() === 1 ? "is" : "are"} still
                      processing
                    </h3>

                    <p class="mb-3 text-sm">
                      Storytell needs to process assets before you can chat with them.
                      <br />
                      If you submit your prompt now, the responses may not be relevant.{" "}
                      <a
                        href="https://go.storytell.ai/file-processing-status"
                        target="_blank"
                        rel="noreferrer"
                        class="text-brand underline text-xs"
                      >
                        Learn more
                      </a>
                    </p>

                    <p class="mb-5">Would you like us to notify you when they're ready?</p>
                    <div class="flex items-center justify-end gap-3">
                      <StButton
                        onClick={() => {
                          wire.ui.processingAssetsGate.executeGatedAction();
                          closeModal();
                        }}
                        class="dark:border-primary-border"
                      >
                        No, submit the prompt anyway
                      </StButton>
                      <StButton
                        icon={TbBellRinging}
                        onClick={onPromptForNotifications}
                        class="text-sm ml-auto px-3 py-2 text-on-brand bg-brand hover:bg-brand-hover"
                      >
                        Yes, notify me
                      </StButton>
                    </div>
                  </Match>
                  <Match when={variant() === "previously-denied"}>
                    <h3 class="text-lg font-medium mb-6">We need your permission to notify you</h3>

                    <p class="mb-2">Go to your browser settings to enable notifications.</p>

                    <StButton
                      icon={TbBellRinging}
                      onClick={onPromptForNotifications}
                      class="text-sm ml-auto px-3 py-2 text-on-brand bg-brand hover:bg-brand-hover"
                    >
                      I've enabled notifications
                    </StButton>
                  </Match>

                  <Match when={variant() === "allowed"}>
                    <h3 class="text-lg font-medium mb-6 flex items-center gap-2">
                      <TbCircleCheck />
                      We'll notify you when the assets are ready
                    </h3>

                    <p class="mb-2">Leave this tab open in your browser to get notified.</p>
                  </Match>
                </Switch>
              </Dialog.Content>
            </div>
          </Dialog.Portal>
        </Dialog>
      )}
    </AnimateChildren>
  );
};
